import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '@/components/ui/card';
import { GripVertical } from 'lucide-react';
import type { Agent } from '@/types/Agents';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import AgentReadinessBadge from '@/components/page-components/project/agents/ui/AgentReadinessBadge';
import {
  getAgentReadiness,
  getUsageMetrics,
  type AgentReadiness,
} from '@/utils/subscriptionUtils';

/** Card outline that echoes the readiness badge in the card header. */
const READINESS_CARD_CLASS: Record<AgentReadiness, string> = {
  ready: '',
  waiting: 'ring-1 ring-amber-300 dark:ring-amber-700/60',
  action_required: 'ring-1 ring-blue-300 dark:ring-blue-700/60',
};

interface AgentsListItemProps {
  agent: Agent;
  onCardClick: (agentId: number) => void;
  enableDrag?: boolean;
}

const formatDuration = (seconds: number | null): string => {
  if (seconds === null) return '0s';
  if (seconds < 60) return `${seconds}s`;

  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m${remainingSeconds}s`;
};

const AgentsListItem: React.FC<AgentsListItemProps> = ({
  agent,
  onCardClick,
  enableDrag = true,
}) => {
  const { t } = useTranslation();

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: agent.id!, disabled: !enableDrag });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  // Calculate total contacts based on unique caller_id and agent_id combinations
  const uniqueContacts = new Set<string>();
  (agent.conversations || []).forEach(conv => {
    const contactKey = `${conv.agent_id}-${conv.caller_id}`;
    uniqueContacts.add(contactKey);
  });
  const totalContacts = uniqueContacts.size;

  const conversationCount = agent.conversations?.length || 0;

  const readiness = getAgentReadiness(agent);
  const isNewAgent = !agent.subscription;
  // Server-side usage is already restricted to billable calls inside the
  // current subscription window; the totals below are all-time activity.
  const { usageSeconds: billableCycleSeconds, hasUsage: hasCycleUsage } =
    getUsageMetrics(agent.subscription);

  // Calculate total duration
  const totalDuration = (agent.conversations || []).reduce(
    (sum, conv) => sum + (conv.duration || 0),
    0
  );

  // Calculate average duration
  const avgDuration =
    conversationCount > 0 ? Math.round(totalDuration / conversationCount) : 0;

  return (
    <Card
      ref={setNodeRef}
      style={style}
      className={`group relative h-full cursor-pointer overflow-hidden rounded-lg bg-card transition-all duration-300 touch-manipulation ${
        isDragging
          ? 'z-50 scale-105 opacity-50 ring-2 ring-ring'
          : READINESS_CARD_CLASS[readiness]
      }`}
      onClick={() => onCardClick(agent.id!)}
    >
      {/* Gradient accent bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-gradient-start to-gradient-end opacity-0 transition-opacity duration-300 [@media(hover:hover)]:group-hover:opacity-100" />

      <CardContent className="p-4 md:p-6 flex flex-col h-full">
        {/* Header */}
        <div className="mb-4 flex items-center gap-2 border-b border-border pb-4 pt-2 md:pt-0">
          {/* Drag Handle */}
          {enableDrag && (
            <div
              {...attributes}
              {...listeners}
              onClick={e => e.stopPropagation()}
              className="flex-shrink-0 -ml-1 cursor-grab rounded p-1 opacity-0 transition-opacity touch-none active:cursor-grabbing group-hover:opacity-100 hover:bg-muted"
            >
              <GripVertical className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
          <h3 className="min-w-0 flex-1 truncate text-xl font-semibold text-foreground transition-colors [@media(hover:hover)]:group-hover:text-primary">
            {agent.name}
          </h3>
          <AgentReadinessBadge readiness={readiness} isNew={isNewAgent} />
        </div>

        {/* Stats List */}
        <div className="flex flex-col gap-3 mt-auto">
          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {t('agent.list_item.total_contacts.translation')}
            </span>
            <span className="text-sm font-medium text-foreground">
              {totalContacts}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {t('agent.list_item.conversations.translation')}
            </span>
            <span className="text-sm font-medium text-foreground">
              {conversationCount}
            </span>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {t('agent.list_item.total_duration.translation')}
            </span>
            <span className="text-sm font-medium text-foreground">
              {formatDuration(totalDuration)}
            </span>
          </div>

          {hasCycleUsage && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {t(
                  'agent.list_item.billable_this_cycle.translation',
                  'Billable this cycle'
                )}
              </span>
              <span className="text-sm font-medium text-foreground">
                {formatDuration(billableCycleSeconds)}
              </span>
            </div>
          )}

          <div className="flex justify-between items-center">
            <span className="text-sm text-muted-foreground">
              {t('agent.list_item.avg_duration.translation')}
            </span>
            <span className="text-sm font-medium text-foreground">
              {formatDuration(avgDuration)}
            </span>
          </div>

          {agent.phone_number && (
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">
                {t('agent.list_item.phone.translation')}
              </span>
              <span className="text-sm font-medium text-foreground">
                {agent.phone_number}
              </span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default AgentsListItem;
